/**
 * Download capture state for PlaywrightClient.
 *
 * @module playwright-client/downloads
 */

import * as fs from 'fs/promises';
import * as path from 'path';
import type { BrowserLogger } from '../logger.js';
import type { CapturedDownload } from './internal-types.js';
import { formatFileSize, guessMimeType } from './download-utils.js';

interface DownloadLike {
  suggestedFilename(): string;
  url(): string;
  saveAs(path: string): Promise<void>;
}

export type DownloadWaitOptions = {
  /** Ignore captures created before this time (epoch ms). */
  sinceMs?: number;
  /** Case-insensitive filename glob. Omit or use `*` for any filename. */
  pattern?: string;
  timeoutMs?: number;
  signal?: AbortSignal;
};

export class DownloadCaptureStore {
  private capturedDownloads: CapturedDownload[] = [];
  private downloadCounter = 0;
  private downloadDir: string | null = null;
  private lastReportedDownloadIndex = 0;

  constructor(
    private readonly tmpBaseDir: string,
    private readonly logger: BrowserLogger
  ) {}

  get directory(): string | null {
    return this.downloadDir;
  }

  get downloads(): CapturedDownload[] {
    return this.capturedDownloads;
  }

  getLastReportedIndex(): number {
    return this.lastReportedDownloadIndex;
  }

  setLastReportedIndex(index: number): void {
    this.lastReportedDownloadIndex = index;
  }

  async prepareDirectory(): Promise<string> {
    this.downloadDir = path.join(this.tmpBaseDir, `playwright-downloads-${Date.now()}`);
    await fs.mkdir(this.downloadDir, { recursive: true });
    return this.downloadDir;
  }

  capture(download: DownloadLike): CapturedDownload {
    const id = ++this.downloadCounter;
    const suggestedFilename = download.suggestedFilename();
    const url = download.url();

    const entry: CapturedDownload = {
      id,
      suggestedFilename,
      url,
      savedPath: null,
      sizeBytes: null,
      completed: false,
      timestamp: new Date().toISOString(),
    };
    this.capturedDownloads.push(entry);

    this.logger.info('[DirectPlaywright] Download started', {
      id,
      suggestedFilename,
      url: url.slice(0, 200),
    });

    if (!this.downloadDir) {
      entry.error = 'No download directory available';
      this.logger.error('[DirectPlaywright] Download failed: no download directory', { id });
      return entry;
    }

    const savePath = path.join(this.downloadDir, `${id}-${suggestedFilename}`);
    download.saveAs(savePath).then(
      async () => {
        try {
          const stat = await fs.stat(savePath);
          entry.savedPath = savePath;
          entry.sizeBytes = stat.size;
          entry.completed = true;
          this.logger.info('[DirectPlaywright] Download completed', {
            id,
            suggestedFilename,
            sizeBytes: stat.size,
            savedPath: savePath,
          });
        } catch {
          entry.savedPath = savePath;
          entry.completed = true;
          this.logger.warn('[DirectPlaywright] Download saved but could not stat file', { id });
        }
      },
      (err: unknown) => {
        entry.error = err instanceof Error ? err.message : String(err);
        this.logger.error('[DirectPlaywright] Download failed', {
          id,
          suggestedFilename,
          error: entry.error,
        });
      }
    );
    return entry;
  }

  /**
   * Persist a file obtained from a browser-observed request when Chromium did
   * not emit a `download` event (for example, a cross-origin attachment that
   * starts a stalled navigation). It deliberately shares the normal capture
   * store so artifact upload and browser_download see identical state.
   */
  async captureBuffer(input: {
    suggestedFilename: string;
    url: string;
    buffer: Buffer;
  }): Promise<CapturedDownload> {
    const id = ++this.downloadCounter;
    const safeFilename = path.basename(input.suggestedFilename).replace(/[^A-Za-z0-9._ -]/g, '_');
    const entry: CapturedDownload = {
      id,
      suggestedFilename: safeFilename || 'download',
      url: input.url,
      savedPath: null,
      sizeBytes: null,
      completed: false,
      timestamp: new Date().toISOString(),
    };
    this.capturedDownloads.push(entry);
    if (!this.downloadDir) {
      entry.error = 'No download directory available';
      return entry;
    }
    const savePath = path.resolve(this.downloadDir, `${id}-${entry.suggestedFilename}`);
    const downloadRoot = `${path.resolve(this.downloadDir)}${path.sep}`;
    if (!savePath.startsWith(downloadRoot)) {
      entry.error = 'Recovered download filename escapes download directory';
      this.logger.error('[DirectPlaywright] Download recovery rejected unsafe filename', { id });
      return entry;
    }
    try {
      await fs.writeFile(savePath, input.buffer);
      entry.savedPath = savePath;
      entry.sizeBytes = input.buffer.length;
      entry.completed = true;
      this.logger.info('[DirectPlaywright] Download recovered from browser request', {
        id,
        suggestedFilename: input.suggestedFilename,
        sizeBytes: input.buffer.length,
      });
    } catch (err) {
      entry.error = err instanceof Error ? err.message : String(err);
    }
    return entry;
  }

  async waitForCompleted(options: DownloadWaitOptions = {}): Promise<CapturedDownload> {
    const timeoutMs = options.timeoutMs ?? 45_000;
    const deadline = Date.now() + timeoutMs;
    while (true) {
      if (options.signal?.aborted) throw new Error('Download wait aborted');
      const match = this.capturedDownloads.find(
        (download) =>
          download.completed &&
          Date.parse(download.timestamp) >= (options.sinceMs ?? 0) &&
          matchesFilename(download.suggestedFilename, options.pattern)
      );
      if (match) return match;
      if (Date.now() >= deadline) {
        const pattern =
          options.pattern && options.pattern !== '*' ? ` matching "${options.pattern}"` : '';
        throw new Error(`No completed download${pattern} was captured within ${timeoutMs}ms.`);
      }
      await new Promise((resolve) => setTimeout(resolve, Math.min(100, deadline - Date.now())));
    }
  }

  async cleanupDirectory(): Promise<void> {
    if (!this.downloadDir) return;
    try {
      await fs.rm(this.downloadDir, { recursive: true, force: true });
      this.logger.debug('[DirectPlaywright] Cleaned up download directory', {
        downloadDir: this.downloadDir,
      });
    } catch (err) {
      this.logger.warn('[DirectPlaywright] Failed to clean up download directory', {
        downloadDir: this.downloadDir,
        error: err instanceof Error ? err.message : String(err),
      });
    }
    this.downloadDir = null;
  }

  reset(): void {
    this.capturedDownloads = [];
    this.downloadCounter = 0;
    this.lastReportedDownloadIndex = 0;
  }

  list(): string {
    if (this.capturedDownloads.length === 0) {
      return 'No downloads captured during this session.';
    }

    const lines: string[] = [`${this.capturedDownloads.length} download(s):`];
    for (const dl of this.capturedDownloads) {
      const size =
        dl.sizeBytes !== null && dl.sizeBytes !== undefined
          ? ` (${formatFileSize(dl.sizeBytes)})`
          : '';
      let status: string;
      if (dl.completed) {
        status = 'completed';
      } else if (dl.error) {
        status = `failed: ${dl.error}`;
      } else {
        status = 'in progress';
      }
      lines.push(`[${dl.id}] ${dl.suggestedFilename}${size} - ${status}`);
    }
    return lines.join('\n');
  }

  async read(downloadId?: number): Promise<{
    filename: string;
    sizeBytes: number;
    mimeType: string;
    textContent: string | null;
  }> {
    if (this.capturedDownloads.length === 0) {
      throw new Error('No downloads captured during this session.');
    }

    const target =
      downloadId !== null && downloadId !== undefined
        ? this.capturedDownloads.find((d) => d.id === downloadId)
        : this.capturedDownloads[this.capturedDownloads.length - 1];

    if (!target) {
      throw new Error(
        `Download #${downloadId} not found. Use browser_list_downloads to see available downloads.`
      );
    }

    if (!target.completed) {
      throw new Error(
        target.error
          ? `Download #${target.id} failed: ${target.error}`
          : `Download #${target.id} is still in progress. Try again shortly.`
      );
    }

    if (!target.savedPath) {
      throw new Error(`Download #${target.id} has no saved file path.`);
    }

    const filename = target.suggestedFilename;
    const sizeBytes = target.sizeBytes ?? 0;
    const mimeType = guessMimeType(filename);

    const ext = path.extname(filename).toLowerCase();
    if (ext === '.pdf') {
      const { extractPdfText } = await import('../pdf-extract.js');
      const { text } = await extractPdfText(target.savedPath);
      return { filename, sizeBytes, mimeType, textContent: text };
    }

    const textExtensions = new Set([
      '.txt',
      '.csv',
      '.json',
      '.xml',
      '.html',
      '.htm',
      '.md',
      '.log',
      '.yml',
      '.yaml',
      '.tsv',
      '.svg',
    ]);
    if (textExtensions.has(ext)) {
      const content = await fs.readFile(target.savedPath, 'utf-8');
      return { filename, sizeBytes, mimeType, textContent: content };
    }

    return { filename, sizeBytes, mimeType, textContent: null };
  }
}

function matchesFilename(filename: string, pattern?: string): boolean {
  if (!pattern || pattern === '*') return true;
  const escaped = pattern
    .replace(/[.+^${}()|[\]\\]/g, '\\$&')
    .replace(/\*/g, '.*')
    .replace(/\?/g, '.');
  return new RegExp(`^${escaped}$`, 'i').test(filename);
}
